import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * One window a member may actually ask for (P25e).
 *
 * ## What it does NOT carry, and why each absence is a decision
 *
 * **No `status`.** The member read returns `open` windows only, so the field
 * would agree with itself on every row — and a client that could read it would
 * eventually branch on it, which is a second place the offering rule lives.
 *
 * **No `capacity`.** How many supervised meetings a partner runs at once is the
 * operator's number. What is *actionable* is how many places are left, which is
 * why the schema carries that instead.
 *
 * **No `createdAt`/`updatedAt`.** When staff opened the window is not a fact
 * about the appointment.
 *
 * ## `remainingCapacity` is ADVISORY, and it is positive by construction
 *
 * There is no booked-count column, deliberately: `venue_availability_slots`'
 * docblock calls capacity *"a ceiling a claim reads, never a count"*, because the
 * authoritative check counts live bookings **under the venue's row lock, in the
 * same statement that inserts** (N27). This number is a read taken outside that
 * lock, so it can be overtaken between the list and the request — the client must
 * still render `booking_slot_full`, and a surface that treats this as a promise
 * is wrong.
 *
 * It is `.positive()` rather than `.nonnegative()` because a full window is
 * **not returned at all**: zero would describe a row the reader filtered out, so
 * making it unrepresentable keeps the payload and the filter from disagreeing.
 *
 * ## Both ends are instants
 *
 * The slot's own rule (`VenueSlotSchema`), unchanged: the api stores UTC wall
 * clock and converts once on read (N3). A member is eventually told this time out
 * loud, and an hour of drift is somebody standing outside a locked door.
 */
export const MemberVenueSlotSchema = z
  .object({
    id: UuidSchema,
    venueId: UuidSchema,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    /** Places left, advisory. See the note above. */
    remainingCapacity: z.number().int().positive(),
  })
  .strict();

export type MemberVenueSlot = z.infer<typeof MemberVenueSlotSchema>;
